import { Component, Inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { catchError, debounceTime, distinctUntilChanged, finalize, of, startWith, Subject, switchMap } from 'rxjs';
import { getSearchTypeLabel, SearchTypeEnum } from '../../../../constants/search.constant';
import { IGroupModel } from '../../../../models/groups.model';
import { IPostModel } from '../../../../models/post.model';
import {
    IGlobalSearchFilterModel,
    IGlobalSearchResult,
    IRecentlyAccessedItem,
    ISearchSummaryModel,
} from '../../../../models/search.model';
import { IUserModel } from '../../../../models/user.model';
import { GeneralService } from '../../../../services/api/general/general.service';
import { LoaderService } from '../../../../services/loader/loader.service';
import { StorageService } from '../../../../services/storage/storage.service';
import { IBasePopupModel } from '../../models/popup.model';
import { BasePopupComponent } from '../base-popup/base-popup.component';
import { SuccessPopupComponent } from '../success-popup/success-popup.component';

@Component({
    selector: 'app-search-popup',
    standalone: false,
    templateUrl: './search-popup.component.html',
    styleUrl: './search-popup.component.scss',
})
export class SearchPopupComponent extends BasePopupComponent {
    protected getSearchTypeLabel = getSearchTypeLabel;
    protected isSearchLoading = signal<boolean>(false);
    protected isSearchSummaryLoading = signal<boolean>(true);
    protected noResultsFound = signal<boolean>(false);
    protected recentlyAccessed = signal<IRecentlyAccessedItem[]>([]);
    protected recentSearches = signal<ISearchSummaryModel[]>([]);
    protected SearchTypeEnum = SearchTypeEnum;
    protected searchValue = signal<string>('');
    protected selectedTab: SearchTypeEnum = SearchTypeEnum.All;
    protected showFilterActions = signal<boolean>(false);
    protected showGroupSearchResult = signal<IGlobalSearchResult>({});
    protected showPostSearchResult = signal<IGlobalSearchResult>({});
    protected showSearchSummary = signal<ISearchSummaryModel[]>([]);
    protected showUserSearchResult = signal<IGlobalSearchResult>({});

    private searchPagination = {
        [SearchTypeEnum.All]: { page: 1, size: 10, allLoaded: false },
        [SearchTypeEnum.User]: { page: 1, size: 10, allLoaded: false },
        [SearchTypeEnum.Tag]: { page: 1, size: 10, allLoaded: false },
        [SearchTypeEnum.Group]: { page: 1, size: 10, allLoaded: false },
    };
    private searchSubject = new Subject<string>();
    private tabSubject = new Subject<SearchTypeEnum>();

    constructor(
        @Inject(MAT_DIALOG_DATA) protected override data: IPostModel,
        protected override dialogRef: MatDialogRef<SearchPopupComponent>,
        private dialog: MatDialog,
        private generalService: GeneralService,
        private loaderService: LoaderService,
        private storageService: StorageService,
    ) {
        super(data, dialogRef);
    }

    static override getDialogConfig(): MatDialogConfig<IBasePopupModel> {
        return {
            maxWidth: '768px',
        };
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.setupSearchSummaryListener();
        this.loadRecentData();
    }

    protected addToRecentlyAccessed(item: IPostModel | IGroupModel | IUserModel, type: SearchTypeEnum): void {
        const recentItem: IRecentlyAccessedItem = {
            data: item,
            type: type,
        };

        this.storageService.addRecentlyAccessed(recentItem);
        this.loadRecentData();
        this.onCancel();
    }

    protected asGroup(item: IRecentlyAccessedItem): IGroupModel {
        return item.data as IGroupModel;
    }

    protected asPost(item: IRecentlyAccessedItem): IPostModel {
        return item.data as IPostModel;
    }

    protected asUser(item: IRecentlyAccessedItem): IUserModel {
        return item.data as IUserModel;
    }

    protected loadMoreSearchResults(reset = false): void {
        const currentTab = this.selectedTab;
        const pagination = this.searchPagination[currentTab];
        pagination.page += 1;

        if (pagination.allLoaded && !reset) {
            return;
        }

        const query = this.searchValue();
        if (!query || !query.trim()) {
            return;
        }

        if (reset) {
            this.resetAllPagination();
            this.showUserSearchResult.set({});
            this.showGroupSearchResult.set({});
            this.showPostSearchResult.set({});
        }

        this.tabSubject.next(currentTab);
    }

    protected override onCancel(): void {
        this.isSearchLoading.set(false);
        this.isSearchSummaryLoading.set(false);
        super.onCancel();
    }

    protected onRecentSearchClick(item: ISearchSummaryModel): void {
        this.searchValue.set(item.name);
        this.showSearchSummary.set([]);
        this.showFilterActions.set(true);

        this.storageService.addRecentSearch(item);
        this.loadRecentData();

        this.setupSearchListener();
    }

    protected onSearchInput(query: string): void {
        this.searchValue.set(query);
        this.searchSubject.next(query);
    }

    protected onSummaryItemClick(item: ISearchSummaryModel): void {
        this.searchValue.set(item.name);
        this.showSearchSummary.set([]);
        this.showFilterActions.set(true);
        this.storageService.addRecentSearch(item);
        this.loadRecentData();
        this.setupSearchListener();
    }

    protected removeRecentSearch(itemToRemove: ISearchSummaryModel): void {
        this.storageService.removeRecentSearch(itemToRemove);
        this.loadRecentData();
    }

    protected removeRecentlyAccessed(item: IRecentlyAccessedItem): void {
        this.storageService.removeRecentlyAccessed(item);
        this.loadRecentData();
    }

    protected selectTab(tab: SearchTypeEnum): void {
        this.selectedTab = tab;
        this.tabSubject.next(tab);
        this.resetAllPagination();
    }

    private loadRecentData(): void {
        this.recentSearches.set(this.storageService.getRecentSearches());
        this.recentlyAccessed.set(this.storageService.getRecentlyAccessed());
    }

    private resetAllPagination(): void {
        for (const key in this.searchPagination) {
            this.searchPagination[key as SearchTypeEnum] = { page: 1, size: 10, allLoaded: false };
        }
    }

    private setupSearchListener(): void {
        this.tabSubject
            .pipe(startWith(this.selectedTab))
            .pipe(
                switchMap((filter) => {
                    const query = this.searchValue();
                    const pagination = this.searchPagination[filter];

                    if (!query || !query.trim()) {
                        this.isSearchLoading.set(false);
                        this.noResultsFound.set(false);
                        this.resetAllPagination();
                        this.showUserSearchResult.set({});
                        this.showGroupSearchResult.set({});
                        this.showPostSearchResult.set({});
                        return of([]);
                    }

                    this.isSearchLoading.set(true);
                    this.loaderService.show();

                    const searchFilter: IGlobalSearchFilterModel = {
                        filter: filter,
                        page: pagination.page,
                        size: pagination.size,
                    };

                    return this.generalService.globalSearch(searchFilter, query).pipe(
                        catchError((error) => {
                            const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                            errorPopupConfig.data = {
                                title: `Error`,
                                message: error.error?.message,
                            };
                            this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                            console.error('Search error:', error);
                            return of([]);
                        }),
                        finalize(() => {
                            this.isSearchLoading.set(false);
                            this.loaderService.hide();
                        }),
                    );
                }),
            )
            .subscribe({
                next: (res) => {
                    document.getElementById('searchInput')?.focus();
                    const response = res as IGlobalSearchResult;
                    const filter = this.selectedTab;
                    const pagination = this.searchPagination[filter];

                    if (!Array.isArray(response) || response.length === 0) {
                        pagination.allLoaded = true;
                        this.noResultsFound.set(true);
                        this.showUserSearchResult.set({});
                        this.showGroupSearchResult.set({});
                        this.showPostSearchResult.set({});
                        return;
                    }

                    // Reset no results flag
                    this.noResultsFound.set(false);

                    // Separate results by type
                    const users = response.filter((item) => item.user);
                    const posts = response.filter((item) => item.post);
                    const groups = response.filter((item) => item.group);

                    // Early return if no filtered results
                    if (users.length === 0 && posts.length === 0 && groups.length === 0) {
                        return;
                    }

                    // Update results - replace for first page, append for pagination
                    if (pagination.page !== 1) {
                        this.showUserSearchResult.update((prev) => ({
                            user: [...(prev.user || []), ...users.map((item) => item.user)],
                        }));
                        this.showPostSearchResult.update((prev) => ({
                            post: [...(prev.post || []), ...posts.map((item) => item.post)],
                        }));
                        this.showGroupSearchResult.update((prev) => ({
                            group: [...(prev.group || []), ...groups.map((item) => item.group)],
                        }));
                        return;
                    }

                    this.showUserSearchResult.set({ user: users.map((item) => item.user) });
                    this.showPostSearchResult.set({ post: posts.map((item) => item.post) });
                    this.showGroupSearchResult.set({ group: groups.map((item) => item.group) });

                    if (response.length < pagination.size) {
                        pagination.allLoaded = true;
                    }
                },
            });
    }

    private setupSearchSummaryListener(): void {
        this.searchSubject
            .pipe(
                debounceTime(400),
                distinctUntilChanged(),
                startWith(''),
                switchMap((query) => {
                    this.searchValue.set(query);
                    this.showFilterActions.set(false);

                    if (!query || !query.trim()) {
                        this.isSearchSummaryLoading.set(false);
                        this.noResultsFound.set(false);
                        this.showSearchSummary.set([]);
                        this.showUserSearchResult.set({});
                        this.showGroupSearchResult.set({});
                        this.showPostSearchResult.set({});
                        return of([]);
                    }

                    this.isSearchSummaryLoading.set(true);
                    this.loaderService.show();

                    const searchFilter: IGlobalSearchFilterModel = {
                        criteria: query,
                        page: 1,
                        size: 50,
                    };

                    return this.generalService.globalSearchSummary(searchFilter, query).pipe(
                        catchError((error) => {
                            const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                            errorPopupConfig.data = {
                                title: `Error`,
                                message: error.error?.message,
                            };
                            this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                            console.error('Search summary error:', error);
                            return of([]);
                        }),
                        finalize(() => {
                            this.isSearchSummaryLoading.set(false);
                            this.loaderService.hide();
                        }),
                    );
                }),
            )
            .subscribe({
                next: (results) => {
                    document.getElementById('searchInput')?.focus();
                    if (!Array.isArray(results) || results.length === 0) {
                        this.noResultsFound.set(
                            Array.isArray(results) && results.length === 0 && this.searchValue().trim() !== '',
                        );
                        this.showSearchSummary.set([]);
                        return;
                    }

                    this.noResultsFound.set(false);
                    this.showSearchSummary.set(results);
                },
            });
    }
}
